import { useContext, useMemo } from "react";
import classNames from "classnames";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { IntegrationAccount } from "@common/types/integrationAccount";
import { ALLOWED_FINANCIAL_ACCOUNT_TYPES_FOR_FILE_IMPORT } from "@common/constants";
import { EntitySelect } from "@components/entity/EntitySelect";
import { FinancialAccountFinancialTypeSelect } from "../financialAccount/FinancialAccountFinancialTypeSelect";
import { ImporterAccountPicker } from "./ImporterAccountPicker";
import { ImporterBankInput } from "./ImporterBankInput";
import { ImporterContext } from "./importer.context";
import { useImporterAccountEditor } from "./useImporterAccountEditor";
import styles from "./ImporterPage.module.scss";

interface Props {
    financialDocument: FinancialDocumentExtended;
}

export const ImporterDocumentInfoSummary: React.FC<Props> = ({
    financialDocument,
}) => {
    const { documentRows, previewedRowId } = useContext(ImporterContext);

    const documentRow = useMemo(
        () => documentRows.find((row) => row.id === previewedRowId),
        [documentRows, previewedRowId],
    );

    if (!documentRow) {
        return null;
    }

    return (
        <ImporterDocumentInfoSummaryContent
            financialDocument={financialDocument}
            integrationAccount={documentRow.integrationAccount}
            documentRowId={documentRow.id}
        />
    );
};

interface ContentProps {
    financialDocument: FinancialDocumentExtended;
    integrationAccount?: IntegrationAccount;
    documentRowId: string;
}

const ImporterDocumentInfoSummaryContent: React.FC<ContentProps> = ({
    financialDocument,
    integrationAccount,
    documentRowId,
}) => {
    const {
        entities,
        selectedFinancialAccount,
        selectedEntity,
        isAccountLinked,
        institutionName,
        financialAccountType,
        isReadOnly,
        disabledReason,
        accountPickerDisabledReason,
        updateAccountInfoField,
        onFinancialAccountSelected,
        onInstitutionNameChanged,
        onAccountTypeChanged,
        onEntityChanged,
    } = useImporterAccountEditor({
        integrationAccount,
        financialDocument,
        documentRowId,
    });

    const isInstitutionNameMissing = !isReadOnly && !institutionName;
    const isAccountTypeMissing = !isReadOnly && !financialAccountType;
    const isEntityMissing = !isReadOnly && !selectedEntity;

    return (
        <section className={styles.documentInfoSummary}>
            <div className={styles.documentInfoField}>
                <label className={styles.documentInfoLabel}>Account #</label>
                <div className={styles.documentInfoInput}>
                    <ImporterAccountPicker
                        accountInfoExtraction={
                            financialDocument.accountInfoExtraction
                        }
                        selectedFinancialAccount={selectedFinancialAccount}
                        entityId={selectedEntity?.id}
                        onAccountInfoChanged={updateAccountInfoField}
                        onFinancialAccountSelected={onFinancialAccountSelected}
                        isAccountLinked={isAccountLinked}
                        disabledReason={accountPickerDisabledReason}
                    />
                </div>
            </div>
            <div className={styles.documentInfoField}>
                <label className={styles.documentInfoLabel}>Bank</label>
                <div className={styles.documentInfoInput}>
                    <ImporterBankInput
                        className={
                            isInstitutionNameMissing
                                ? styles.warningInput
                                : undefined
                        }
                        value={institutionName ?? ""}
                        onChange={onInstitutionNameChanged}
                        disabledReason={disabledReason}
                    />
                </div>
            </div>
            <div className={styles.documentInfoField}>
                <label className={styles.documentInfoLabel}>Account type</label>
                <div
                    className={classNames(styles.documentInfoInput, {
                        [styles.invalidSelectTrigger]: isAccountTypeMissing,
                    })}
                >
                    <FinancialAccountFinancialTypeSelect
                        value={financialAccountType ?? undefined}
                        onChange={onAccountTypeChanged}
                        allowedTypes={
                            ALLOWED_FINANCIAL_ACCOUNT_TYPES_FOR_FILE_IMPORT
                        }
                        disabledReason={disabledReason}
                    />
                </div>
            </div>
            <div className={styles.documentInfoField}>
                <label className={styles.documentInfoLabel}>Entity</label>
                <div
                    className={classNames(styles.documentInfoInput, {
                        [styles.invalidSelectTrigger]: isEntityMissing,
                    })}
                >
                    <EntitySelect
                        entities={entities}
                        selectedEntity={selectedEntity}
                        onChange={onEntityChanged}
                        disabledReason={disabledReason}
                        triggerButtonProps={{
                            variant: "primary",
                            block: true,
                        }}
                    />
                </div>
            </div>
        </section>
    );
};
